import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard,
  formatPortalDate
} from './portalShared'
import SubcontractorSiteDetailDrawer from './SubcontractorSiteDetailDrawer'

const DEFAULT_CENTER = [33.55, -112.0]

const URGENCY_COLORS = {
  overdue: 'var(--df-danger-text, #a9341d)',
  due_soon: 'var(--df-accent, #c66a45)',
  needs_completion: 'var(--df-warn-text, #8c5b00)',
  ok: '#9bb88f',
  never_done: 'var(--df-ink-4, #97a094)',
  ignored: 'var(--df-ink-4, #97a094)'
}

const useStyles = makeStyles((theme) => ({
  filterBar: {
    background: 'var(--df-surface)',
    border: '1px solid var(--df-line)',
    borderRadius: 12,
    padding: 14,
    marginBottom: 14,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 12,
    flexWrap: 'wrap'
  },
  mapCard: {
    borderRadius: 'var(--df-radius-md)',
    overflow: 'hidden',
    boxShadow: 'var(--df-shadow-sm)',
    border: '1px solid var(--df-line)'
  },
  mapContainer: {
    width: '100%',
    minHeight: 680,
    height: 680,
    backgroundColor: 'var(--df-bg)'
  },
  selectAllRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 14px',
    fontSize: 12,
    color: 'var(--df-ink-3)',
    marginBottom: 10
  },
  siteCard: {
    position: 'relative',
    borderRadius: 12,
    border: '1px solid var(--df-line)',
    background: 'var(--df-surface)',
    cursor: 'pointer',
    transition: 'border-color .12s, box-shadow .12s',
    height: '100%',
    '&:hover': {
      borderColor: 'var(--df-line-2)',
      boxShadow: '0 4px 14px rgba(28,36,25,.08)'
    }
  },
  siteCardSelected: {
    borderColor: 'var(--df-primary) !important',
    boxShadow: '0 0 0 3px rgba(31,94,58,.14) !important'
  },
  urgencyRule: {
    position: 'absolute',
    left: 0,
    top: 14,
    bottom: 14,
    width: 3,
    borderRadius: 2
  },
  cardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10
  },
  cardTitleBlock: {
    flex: 1,
    minWidth: 0
  },
  siteName: {
    fontSize: 14.5,
    fontWeight: 700,
    color: 'var(--df-ink)',
    lineHeight: 1.25,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  siteAddress: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 11,
    color: 'var(--df-ink-3)',
    marginTop: 3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  mapPin: {
    width: 22,
    height: 22,
    borderRadius: 6,
    background: 'var(--df-surface-2)',
    border: '1px solid var(--df-line)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--df-ink-3)',
    fontSize: 11,
    flexShrink: 0,
    marginTop: 1
  },
  mapPinUnmapped: {
    color: 'var(--df-warn-text, #8c5b00)',
    background: 'var(--df-warn-bg, #fff3d9)',
    borderColor: '#d9b97a'
  },
  chipRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginTop: 10
  },
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 10,
    borderTop: '1px dashed var(--df-line)',
    fontSize: 12,
    color: 'var(--df-ink-3)',
    marginTop: 10
  },
  metaLabel: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 10,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--df-ink-4)',
    marginRight: 6
  },
  metaValue: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 12,
    color: 'var(--df-ink-2)',
    fontVariantNumeric: 'tabular-nums'
  },
  metaValueDim: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 12,
    color: 'var(--df-ink-4)',
    fontVariantNumeric: 'tabular-nums'
  },
  cardPrompt: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--df-primary)',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': { textDecoration: 'underline' }
  },
  bulkBar: {
    position: 'fixed',
    left: '50%',
    bottom: 24,
    transform: 'translateX(-50%)',
    background: 'var(--df-ink)',
    color: '#f4efe4',
    borderRadius: 14,
    padding: '10px 14px 10px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    boxShadow: '0 18px 48px rgba(28,36,25,.32), 0 0 0 1px rgba(255,255,255,.06) inset',
    zIndex: 50
  },
  bulkCount: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 13,
    color: '#fff',
    fontWeight: 600
  },
  bulkLabel: {
    fontSize: 13,
    color: '#c8d0c4'
  },
  bulkSep: {
    width: 1,
    height: 22,
    background: 'rgba(255,255,255,.12)'
  }
}))

const assignmentViewOptions = [
  { value: 'all', label: 'All visible sites' },
  { value: 'assigned', label: 'Assigned only' },
  { value: 'unassigned', label: 'Unassigned only' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'unmapped', label: 'Unmapped' }
]

const sortOptions = [
  { value: 'nextScheduledDate', label: 'Urgency · due date' },
  { value: 'lastAppliedDate', label: 'Last applied' },
  { value: 'siteName', label: 'Property name' },
  { value: 'address', label: 'Address (A-Z)' }
]

function getUrgencyColor (status) {
  return URGENCY_COLORS[status] || URGENCY_COLORS.ok
}

function getUrgencyChip (item) {
  if (item.status === 'overdue') {
    const days = item.daysOverdue || item.daysLate || '?'
    return { label: `Overdue ${days}d`, className: 'df-chip-danger' }
  }
  if (item.status === 'due_soon') {
    const days = item.daysTilDue || '?'
    return { label: `Due in ${days}d`, className: 'df-chip-warn' }
  }
  if (item.status === 'needs_completion') {
    return { label: 'Needs completion', className: 'df-chip-warn' }
  }
  if (item.status === 'never_done') {
    return { label: 'Never applied', className: 'df-chip-warn' }
  }
  if (item.effectiveNextDate) {
    return { label: `Due ${formatPortalDate(item.effectiveNextDate, { fallback: '' })}`, className: '' }
  }
  return null
}

function getAssignmentChip (item) {
  if (item.assignedCrewName) {
    return { label: `Crew · ${item.assignedCrewName}`, className: 'df-chip-ok' }
  }
  return { label: 'Unassigned', className: 'df-chip-warn' }
}

function getCardPrompt (item) {
  if (!item.lastAppliedDate && !item.effectiveNextDate) return 'Schedule first →'
  if (!item.assignedCrewName) return 'Assign crew →'
  if (item.status === 'overdue') return 'Reschedule →'
  return 'View site →'
}

function buildMarkerIcon (assigned = false) {
  const color = assigned ? '#1f5e3a' : '#8a692d'
  return L.divIcon({
    className: '',
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.25);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8]
  })
}

const SubcontractorJobsites = ({ user, notification }) => {
  const classes = useStyles()
  const isAdmin = user?.subcontractorProfile?.role === 'admin'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [summary, setSummary] = useState({})
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [assignmentView, setAssignmentView] = useState('all')
  const [sortBy, setSortBy] = useState('nextScheduledDate')
  const [viewMode, setViewMode] = useState('list')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [selectedScheduleItemId, setSelectedScheduleItemId] = useState(null)
  const [crews, setCrews] = useState([])
  const [bulkCrewId, setBulkCrewId] = useState('')
  const [bulkAssigning, setBulkAssigning] = useState(false)
  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  const markersRef = useRef([])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim())
    }, 250)
    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  const loadJobsites = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.get('/api/subcontractor/jobsites', {
        params: { search, sortBy, sortOrder: 'asc', assignmentView: isAdmin ? assignmentView : 'assigned' }
      })
      setItems(response.data?.items || [])
      setSummary(response.data?.summary || {})
    } catch (requestError) {
      console.error('Failed to load subcontractor jobsites', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load subcontractor jobsites right now.')
    } finally {
      setLoading(false)
    }
  }, [assignmentView, isAdmin, search, sortBy])

  useEffect(() => { loadJobsites() }, [loadJobsites])

  const loadCrews = useCallback(async () => {
    if (!isAdmin) return
    try {
      const response = await axios.get('/api/subcontractor/crews')
      setCrews((response.data?.items || []).filter(c => c.isActive !== false))
    } catch (e) {
      console.error('Failed to load crews for bulk assign', e)
    }
  }, [isAdmin])

  useEffect(() => { loadCrews() }, [loadCrews])

  // Map setup — re-run when viewMode changes so the map initializes
  // after the container div is rendered
  useEffect(() => {
    if (viewMode !== 'map' || !mapContainerRef.current || mapRef.current) return
    const map = L.map(mapContainerRef.current, { center: DEFAULT_CENTER, zoom: 11, zoomControl: true })
    L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
      attribution: '&copy; Google Maps', maxZoom: 20, minZoom: 0
    }).addTo(map)
    mapRef.current = map
    window.requestAnimationFrame(() => map.invalidateSize())
    return () => { map.remove(); mapRef.current = null; markersRef.current = [] }
  }, [viewMode])

  useEffect(() => {
    if (!mapRef.current || viewMode !== 'map') return
    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []
    const mappedItems = items.filter(item => Number.isFinite(item.latitude) && Number.isFinite(item.longitude))
    const bounds = []
    mappedItems.forEach((item) => {
      const marker = L.marker([item.latitude, item.longitude], { icon: buildMarkerIcon(item.isAssigned) })
      marker.bindPopup(`<div style="min-width:220px"><strong>${item.siteName || 'Unnamed Site'}</strong><br/>${item.address || 'No address'}<br/>${item.assignedCrewName || 'Unassigned'}</div>`)
      marker.addTo(mapRef.current)
      markersRef.current.push(marker)
      bounds.push([item.latitude, item.longitude])
    })
    window.requestAnimationFrame(() => {
      if (!mapRef.current) return
      mapRef.current.invalidateSize()
      if (bounds.length) mapRef.current.fitBounds(bounds, { padding: [40, 40] })
      else mapRef.current.setView(DEFAULT_CENTER, 11)
    })
  }, [items, viewMode])

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set())
    else setSelectedIds(new Set(items.map(i => i._id)))
  }

  const handleBulkAssign = async () => {
    if (!bulkCrewId || !selectedIds.size) return
    setBulkAssigning(true)
    try {
      await Promise.all([...selectedIds].map(id =>
        axios.post('/api/subcontractor/schedule/assign-crew', { scheduleItemId: id, crewId: bulkCrewId })
      ))
      notification?.({ type: 'success', title: 'Bulk assign complete', message: `Assigned ${selectedIds.size} sites.` })
      setSelectedIds(new Set())
      setBulkCrewId('')
      await loadJobsites()
    } catch (e) {
      notification?.({ type: 'danger', title: 'Bulk assign failed', message: e?.response?.data?.message || 'Some assignments may have failed.' })
    } finally {
      setBulkAssigning(false)
    }
  }

  const unassignedCount = summary.unassigned || 0
  const overdueCount = summary.overdue || 0

  const summaryCards = useMemo(() => ([
    { eyebrow: 'Visible sites', value: summary.total || 0, caption: 'Total properties on your list' },
    { eyebrow: 'Mapped', value: `${summary.mapped || 0}`, caption: `${(summary.total || 0) - (summary.mapped || 0)} missing coordinates` },
    { eyebrow: 'Unassigned', value: unassignedCount, caption: unassignedCount > 0 ? 'All sites need a crew' : 'Every site has a crew', alert: unassignedCount > 0 },
    { eyebrow: 'Overdue', value: overdueCount, caption: 'Past target application date', alert: overdueCount > 0 }
  ]), [summary, unassignedCount, overdueCount])

  if (loading && !items.length) {
    return <PortalLoadingCard title="Loading jobsites" message="Loading your properties..." minHeight={280} />
  }

  if (error && !items.length) {
    return <PortalStateCard title="Jobsites unavailable" message={error} actionLabel="Retry" onAction={loadJobsites} tone="danger" minHeight={240} />
  }

  return (
    <div>
      {/* Stats */}
      <Grid container spacing={2}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.eyebrow}>
            <PortalMetricCard eyebrow={card.eyebrow} value={card.value} caption={card.caption} borderColor={card.alert ? 'var(--df-warn-text, #8c5b00)' : undefined} />
          </Grid>
        ))}
      </Grid>

      {/* Filter bar */}
      <Box mt={2}>
        <div className={classes.filterBar}>
          <div style={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Address, property name, or community"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              variant="outlined"
            />
          </div>
          <div>
            <TextField
              select
              label="Sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              variant="outlined"
              style={{ minWidth: 180 }}
            >
              {sortOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </div>
          <div>
            <TextField
              select
              label="Show"
              value={assignmentView}
              onChange={(e) => setAssignmentView(e.target.value)}
              variant="outlined"
              style={{ minWidth: 180 }}
            >
              {assignmentViewOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'inline-flex', background: 'var(--df-bg-2)', border: '1px solid var(--df-line)', borderRadius: 10, padding: 3, height: 34, alignItems: 'center' }}>
            <button type="button" onClick={() => setViewMode('list')} style={{ height: 26, padding: '0 14px', border: 'none', background: viewMode === 'list' ? 'var(--df-surface)' : 'transparent', color: viewMode === 'list' ? 'var(--df-ink)' : 'var(--df-ink-3)', fontSize: 12, fontWeight: 600, borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', boxShadow: viewMode === 'list' ? '0 1px 2px rgba(28,36,25,.06)' : 'none' }}>
              List
            </button>
            <button type="button" onClick={() => setViewMode('map')} style={{ height: 26, padding: '0 14px', border: 'none', background: viewMode === 'map' ? 'var(--df-surface)' : 'transparent', color: viewMode === 'map' ? 'var(--df-ink)' : 'var(--df-ink-3)', fontSize: 12, fontWeight: 600, borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', boxShadow: viewMode === 'map' ? '0 1px 2px rgba(28,36,25,.06)' : 'none' }}>
              Map
            </button>
          </div>
        </div>
      </Box>

      {viewMode === 'map' ? (
        <Card className={classes.mapCard}>
          <div ref={mapContainerRef} className={classes.mapContainer} />
        </Card>
      ) : (
        <>
          {/* Select all */}
          {isAdmin && items.length > 0 && (
            <div className={classes.selectAllRow}>
              <Checkbox
                size="small"
                color="primary"
                checked={selectedIds.size === items.length && items.length > 0}
                indeterminate={selectedIds.size > 0 && selectedIds.size < items.length}
                onChange={toggleSelectAll}
                style={{ padding: 0 }}
              />
              <span>Select all on page</span>
              <span style={{ color: 'var(--df-ink-4)' }}>·</span>
              <span>Showing {items.length} of {summary.total || items.length}</span>
            </div>
          )}

          {/* Card grid */}
          <Grid container spacing={2}>
            {items.map((item) => {
              const isSelected = selectedIds.has(item._id)
              const urgencyChip = getUrgencyChip(item)
              const assignmentChip = getAssignmentChip(item)
              const prompt = getCardPrompt(item)
              const isMapped = Number.isFinite(item.latitude) && Number.isFinite(item.longitude)

              return (
                <Grid item xs={12} md={6} lg={4} key={item._id}>
                  <Card
                    className={`${classes.siteCard} ${isSelected ? classes.siteCardSelected : ''}`}
                    elevation={0}
                    onClick={() => setSelectedScheduleItemId(item.scheduleItemId || item._id)}
                  >
                    {/* Urgency left rule */}
                    <div className={classes.urgencyRule} style={{ background: getUrgencyColor(item.status) }} />

                    <CardContent style={{ paddingLeft: 18 }}>
                      {/* Top row: checkbox + title + map pin */}
                      <div className={classes.cardTop}>
                        {isAdmin && (
                          <Checkbox
                            size="small"
                            color="primary"
                            checked={isSelected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleSelect(item._id)}
                            style={{ padding: 0, marginTop: 3 }}
                          />
                        )}
                        <div className={classes.cardTitleBlock}>
                          <div className={classes.siteName}>
                            {item.siteName || 'Unnamed Site'}
                          </div>
                          <div className={classes.siteAddress}>
                            {item.address || 'No address'}
                          </div>
                        </div>
                        <div className={`${classes.mapPin} ${!isMapped ? classes.mapPinUnmapped : ''}`} title={isMapped ? 'Mapped' : 'Unmapped'}>
                          {isMapped ? '●' : '⚠'}
                        </div>
                      </div>

                      {/* Chips: max 2 — urgency + assignment */}
                      <div className={classes.chipRow}>
                        {urgencyChip && <Chip size="small" label={urgencyChip.label} className={urgencyChip.className} />}
                        <Chip size="small" label={assignmentChip.label} className={assignmentChip.className} />
                      </div>

                      {/* Meta footer */}
                      <div className={classes.cardMeta}>
                        <div>
                          <span className={classes.metaLabel}>Last</span>
                          <span className={item.lastAppliedDate ? classes.metaValue : classes.metaValueDim}>
                            {formatPortalDate(item.lastAppliedDate, { fallback: 'No prior application' })}
                          </span>
                        </div>
                        <button
                          type="button"
                          className={classes.cardPrompt}
                          onClick={(e) => { e.stopPropagation(); setSelectedScheduleItemId(item.scheduleItemId || item._id) }}
                        >
                          {prompt}
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>

          {!items.length && (
            <PortalStateCard title="No jobsites match filters" message="Try adjusting your search or filter settings." minHeight={200} />
          )}
        </>
      )}

      {/* Bulk action bar */}
      {isAdmin && selectedIds.size > 0 && (
        <div className={classes.bulkBar}>
          <span className={classes.bulkCount}>{selectedIds.size} selected</span>
          <span className={classes.bulkSep} />
          <span className={classes.bulkLabel}>Assign to</span>
          <select
            value={bulkCrewId}
            onChange={(e) => setBulkCrewId(e.target.value)}
            style={{ background: 'rgba(255,255,255,.08)', color: '#f4efe4', border: '1px solid rgba(255,255,255,.14)', height: 30, padding: '0 26px 0 10px', borderRadius: 8, fontSize: 12, fontFamily: 'inherit', appearance: 'none', cursor: 'pointer' }}
          >
            <option value="">Choose crew…</option>
            {crews.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <button
            type="button"
            onClick={handleBulkAssign}
            disabled={!bulkCrewId || bulkAssigning}
            style={{ height: 30, padding: '0 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer', background: 'var(--df-primary)', color: '#fff', border: '1px solid var(--df-primary-2, #164a2c)', fontFamily: 'inherit' }}
          >
            {bulkAssigning ? 'Assigning…' : 'Assign'}
          </button>
          <span className={classes.bulkSep} />
          <button
            type="button"
            onClick={() => { setSelectedIds(new Set()); setBulkCrewId('') }}
            style={{ background: 'none', border: 'none', color: '#a8b8a4', fontSize: 16, cursor: 'pointer', padding: 4, fontFamily: 'inherit' }}
            title="Clear selection"
          >
            ×
          </button>
        </div>
      )}

      <SubcontractorSiteDetailDrawer
        scheduleItemId={selectedScheduleItemId}
        open={Boolean(selectedScheduleItemId)}
        onClose={() => setSelectedScheduleItemId(null)}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorJobsites)
